import { Injectable } from '@angular/core';
import { ModeModelBase } from '../model';
import { ModelAccessorBase, ModelAccessorInjectables } from '../model_accessor';

function initializeLink(res, link, fallback) {
  if (res && typeof res.hasLink === 'function' && res.hasLink(link)) {
    return res.links(link).href;
  } else {
    return fallback;
  }
}

export class AccountModel extends ModeModelBase {
  override _name = 'account';

  newReportLink: any;
  activityLink: any;
  searchLink: any;
  starredLink: any;
  discoverLink: any;
  explorationsLink: any;
  inviteLink: any;
  uploadLink: any;
  homeLink: any;
  reportsLink: any;
  datasourceLink: any;
  accountLink: any;
  billingLink: any;
  settingsLink: any;
  slackSettingsLink: any;
  publicDSLink: any;
  spacesIndexLink: any;
  groupsIndexLink: any;
  newOrganizationLink: any;
  datasourceIndexLink: any;
  themesSettingsLink: any;
  colorPalettesLink: any;
  memberSessionTimeoutLink: any;
  membershipType: string;
  paymentMethodConfirmed: boolean;
  privateDefinitionCount: any;
  privateDefinitionLimit: any;
  plan_code: string;
  name: any;
  username: any;
  user: any;

  override idAttribute = 'username';
  override hasMany = [
    { name: 'reports', idAttribute: 'token' },
    { name: 'reports', as: 'starredReports', idAttribute: 'token' },
    { name: 'reports', as: 'draftsReports', idAttribute: 'token' },
    { name: 'reports', as: 'viewedReports', idAttribute: 'token' },
    { name: 'reports', as: 'starredBaseReports', idAttribute: 'token' },
    { name: 'reports', as: 'viewedBaseReports', idAttribute: 'token' },
    { name: 'dataSources', idAttribute: 'token' },
    { name: 'tables', idAttribute: 'id' },
    { name: 'organizations', idAttribute: 'username' },
    { name: 'spaces', idAttribute: 'token' },
    { name: 'groups', idAttribute: 'token' },
    { name: 'spaces', as: 'customSpaces', idAttribute: 'token' },
    { name: 'spaces', as: 'moveToSpaces', idAttribute: 'token' },
    { name: 'definitions', idAttribute: 'token' },
    { name: 'accounts', as: 'admins', idAttribute: 'username' },
    { name: 'colorPalettes', idAttribute: 'token' },
    { name: 'invites' },
  ];
  override hasOne = [
    { name: 'preference', idAttribute: 'id' },
    { name: 'slackApp', idAttribute: 'id' },
  ];
  override attributes = [
    { name: 'name', default: '' },
    { name: 'id', default: null },
    { name: 'public', default: true },
    { name: 'avatar', default: {} },
    { name: 'email', default: '' },
    { name: 'token', default: '' },
    { name: 'user', default: false },
    { name: 'email_verified', default: false },
    { name: 'authorized_domains', default: [] },
    { name: 'plan_code', default: 'standard' },
    { name: 'membershipType', default: 'limited' },
    { name: 'trialState', default: 'pending' },
    { name: 'paymentMethodConfirmed', default: false },
    { name: 'privateDefinitionCount', default: 0 },
    { name: 'privateDefinitionLimit', default: 0 },
    { name: 'space_count', default: 0 },
    { name: 'data_source_count', default: 0 },
    { name: 'organizations_count', default: null },
    { name: 'dataset_size_limit_mb', default: 0 },
    { name: 'adminDataSourceConnectionsOnly', default: false },
    { name: 'adminCollectionCreationOnly', default: false },
  ];
  override initialize(resource) {
    this.newReportLink = initializeLink(resource, 'new_report_web', '#');
    this.activityLink = initializeLink(resource, 'home_activity_web', '#');
    this.searchLink = initializeLink(resource, 'home_search_web', '#');
    this.starredLink = initializeLink(resource, 'home_starred_web', '#');
    this.discoverLink = initializeLink(resource, 'home_discover_web', '#');
    this.explorationsLink = initializeLink(resource, 'home_explorations_web', '#');
    this.inviteLink = initializeLink(resource, 'new_invite_web', '#');
    this.uploadLink = initializeLink(resource, 'new_upload_web', '#');
    this.homeLink = initializeLink(resource, 'home_web', '#');
    this.reportsLink = initializeLink(resource, 'home_reports_web', '#');
    this.datasourceLink = initializeLink(resource, 'select_data_sources_web', '#');
    this.accountLink = initializeLink(resource, 'web', '#');
    this.billingLink = initializeLink(resource, 'billing_web', '#');
    this.settingsLink = initializeLink(resource, 'web_settings', '/settings');
    this.slackSettingsLink = initializeLink(resource, 'web_settings_slack', '#');
    this.publicDSLink = initializeLink(resource, 'web_public_datasource_home', '#');
    this.spacesIndexLink = initializeLink(resource, 'web_spaces', '#');
    this.groupsIndexLink = initializeLink(resource, 'web_groups', '#');
    this.newOrganizationLink = initializeLink(resource, 'web_new_organization', '#');
    this.datasourceIndexLink = initializeLink(resource, 'web_data_sources_settings', '#');
    this.themesSettingsLink = initializeLink(resource, 'web_settings_themes', '#');
    this.colorPalettesLink = initializeLink(resource, 'web_color_palettes_settings', '#');
    this.memberSessionTimeoutLink = initializeLink(resource, 'member_session_timeout', '#');
  }

  isAdmin() {
    return this.membershipType === 'admin';
  }
  canDismissPaywallBanner() {
    return this.paymentMethodConfirmed && this.isAdmin();
  }
  isAtDefinitionLimit() {
    return this.privateDefinitionCount >= this.privateDefinitionLimit && this.plan_code === 'standard';
  }
  displayName() {
    return this.name || this.username;
  }
  accountName() {
    return this.user ? 'Mode Community' : this.name;
  }
}

@Injectable({ providedIn: 'root' })
export class AccountModelAccessor extends ModelAccessorBase<AccountModel> {
  override name = 'account';
  override idAttribute = 'username';
  override getIdFromLink = (link) => {
    return link.split('/api/')[1].split('?')[0];
  };

  constructor(injectables: ModelAccessorInjectables) {
    super(injectables, 'migrated', 'mode.account');
  }

  createModel(resource, opts?) {
    const model = new AccountModel(this.injectables.modelInjectables);
    model.$initialize(resource, opts);
    return model;
  }
}
